// =============================================================================
//  Copyright : 2019 semantics GmbH
//  Date      : $Date: 2026-05-27 14:18:05 +0200 (Wed, 27 May 2026) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 163319 $
//  Purpose   : react hooks for tree component
// =============================================================================
// ---Third-party imports
// ---Framework imports
import { Actions, Sel } from 'uix/registries';
import { size, get, keys, has, flatten, values, merge, isNil, dict, mapItems } from 'ldash';
import log from 'uix/logging';
import { WikiTemplates } from 'uix/registry';
import { useSelector, useApi, useChunk, usePhysicalNode, useActions, useState, useEffect, useMemo, usePartContext, useDebounce, usePrevious } from 'uix/hooks';
import { useDomainTypes } from 'uix/types/hooks';
// ---Local imports
import { MIME } from './utils';
import { resolveWidgets } from './resolve';
import { useVlmContext } from './vlm';
// ---Exports
export const buildWikiTemplates = (widgetSchema)=>{
    /**
   * Constructs a dictionary of wiki templates with added schema information based on a given widget schema.
   *
   * This function iterates over a predefined collection of wiki templates, checking if each template
   * has a matching tag in the provided widget schema. For the templates with a match, it augments them
   * with a schema object containing both grouped items by their original categorization and a flattened
   * dictionary for direct item name access. It keeps a count of how many templates were successfully
   * matched and handled.
   *
   * Additionally, if the number of unhandled items in the widget schema exceeds an expected threshold of 4
   * (accounting for exhibition, room, slide, and subslide), it logs a warning with the count of unhandled
   * items and the names of unhandled widget schema items.
   *
   * @param {Object} widgetSchema - The schema containing widget configurations, keyed by a unique identifier
   * for each widget.
   * @returns {Object} A dictionary of wiki templates augmented with a schema derived from the widgetSchema
   * for templates that have a corresponding match.
   */ let handled = 0;
    let wts = dict(mapItems(WikiTemplates, (name, wt)=>{
        let matchedTag = wt.tagMatcher;
        if (matchedTag) {
            let schema = widgetSchema[matchedTag];
            if (schema) {
                let byNameLowerCase = dict(flatten(mapItems(schema, (group, items)=>items.map((i)=>[
                            i.name.toLowerCase(),
                            i
                        ]))));
                let completionAttrs = dict(values(byNameLowerCase).map((item)=>[
                        item.name,
                        item.values?.filter((e)=>e !== null) ?? [
                            item.default ?? ''
                        ]
                    ]));
                let completion = ()=>({
                        '!top': [
                            matchedTag
                        ],
                        [matchedTag]: {
                            attrs: completionAttrs
                        }
                    });
                wt = {
                    ...wt,
                    completion,
                    schema: {
                        groups: schema,
                        byNameLowerCase
                    }
                };
                handled += 1;
            }
        //~ log.x('handle %o', matchedTag)
        }
        return [
            name,
            wt
        ];
    }));
    // check if schema contains unprocessed stuff
    // 5 presentation structs are expected (see presentation.constants.SLIDE_TYPES)
    // +2 widgets (AnnotationItem, Tile) due to missing tagMatcher
    if (handled != size(widgetSchema) && size(widgetSchema) - handled > size(expected_unhandled)) {
        let tags = new Set(values(WikiTemplates).map((wt)=>wt.tagMatcher).filter(Boolean));
        log.warn('unhandled widgets %o', keys(widgetSchema).filter((name)=>!tags.has(name) && !expected_unhandled.has(name)));
    }
    // XXX: combine both
    return wts;
};
let expected_unhandled = new Set([
    'Exhibition',
    'Room',
    'Scene',
    'Footer',
    'Subscene',
    'AnnotationItem',
    'Tile',
    'Blog',
    'GYear',
    'LinkCopyright',
    'LinkOwner',
    'ClassificationShowcase',
    'Widget',
    'LinkHome',
    'Readmore',
    'LinkImprint',
    'LinkContact',
    'LinkCollection',
    'mainsearch',
    'LinkNewEntries',
    'Quicksearch',
    'LinkTitles',
    'MobileSearchbox',
    'LinkQuicksearch',
    'LinkPrivacy',
    'LinkAccessibility',
    'LinkTermsOfUse',
    'Classification',
    'ClassificationSlider',
    'DateInput',
    'LinkHelp',
    'vls.buildLink',
    'DomainDescriptionHeader',
    'LinkDetailedsearch',
    'linkHome'
]);
export const useWikiTemplates = ()=>{
    let widgetSchema = useSelector((root)=>root.config.widgetSchema);
    return useMemo(()=>buildWikiTemplates(widgetSchema), [
        widgetSchema,
        size(WikiTemplates)
    ]);
};
export const useWikiRendered = ({ otid, ident })=>{
    let params = ident ? {
        ident
    } : {
        id: otid
    };
    let { data, error } = useApi('filter/getWikiRendered', params);
    return data?.wiki;
};
// example completion cfg
/*
var dummy = {
  attrs: {
    color: ['red', 'green', 'blue', 'purple', 'white', 'black', 'yellow'],
    size: ['large', 'medium', 'small'],
    description: null,
  },
  children: [],
}

let tags = {
  '!top': ['top'],
  '!attrs': {
    id: null,
    class: ['A', 'B', 'C'],
  },
  top: {
    attrs: {
      lang: ['en', 'de', 'fr', 'nl'],
      freeform: null,
    },
    children: ['animal', 'plant'],
  },
  animal: {
    attrs: {
      name: null,
      isduck: ['yes', 'no'],
    },
    children: ['wings', 'feet', 'body', 'head', 'tail'],
  },
  plant: {
    attrs: {name: null},
    children: ['leaves', 'stem', 'flowers'],
  },
  wings: dummy,
  feet: dummy,
  body: dummy,
  head: dummy,
  tail: dummy,
  leaves: dummy,
  stem: dummy,
  flowers: dummy,
}
*/ export const buildCompleter = (WikiTemplates, EditorUtils, env, mimetype)=>{
    /* Example completion
  completion: () => ({
    '!top': ['ReleaseHistory'],
    ReleaseHistory: {
      attrs: {
        max: ['5', '10', '20'],
        limit: [30],
      },
      children: [],
    },
  })
  */ let more = values(WikiTemplates).filter((t)=>t.completion).map((t)=>t.completion(env)) // run evaluation func
    .filter((t)=>t) // filter undefined/null results
    ;
    let toplevelTags = flatten(more.map((cfg)=>cfg['!top'] || []));
    let tagSpecs = merge(...more);
    let tags = {
        ...tagSpecs,
        ['!top']: toplevelTags
    };
    let omitQuote = [
        MIME.MARKDOWN,
        MIME.HTML
    ].includes(mimetype);
    return {
        extraKeys: EditorUtils.completionKeysXML?.(tags),
        hintOptions: {
            schemaInfo: tags,
            matchInMiddle: true,
            completeSingle: false,
            completeOnSingleClick: false,
            omitQuote
        }
    };
};
export const useCompletion = ({ mimetype })=>{
    let EditorUtils = useChunk('EditorUtils');
    if (!EditorUtils) {
        EditorUtils = {
            completionKeysXML: ()=>undefined
        };
    }
    let jobSpec = useSelector((root)=>root.config.jobSpec);
    let classCfg = useSelector(Sel.domainClassCfgs);
    let wikiTemplates = useWikiTemplates();
    let env = {
        classCfg,
        jobSpec
    };
    return buildCompleter(wikiTemplates, EditorUtils, env, mimetype);
};
export const useWidgetResolving = ({ otid, rendered })=>{
    let phys_node = usePhysicalNode();
    let isPhysOtid = otid && phys_node?.otid == otid;
    let isActiveType = isPhysOtid && (phys_node.datatype == 21 || phys_node.datatype == 2);
    let { data } = useApi('wiki/getWidgets', {
        otid
    }, {
        condition: isActiveType
    });
    //~ data.widgets
    let { getType } = useDomainTypes();
    let descendantAssets = null;
    if (isActiveType && data) {
        let node = {
            otid,
            datatype: phys_node.datatype,
            subtype: phys_node.subtype,
            content: {
                rendered
            },
            meta: {}
        };
        let nodes = [
            node,
            ...data.widgets
        ];
        nodes = nodes.map((n)=>({
                ...n,
                type: getType(n.datatype, n.subtype)
            }));
        nodes = resolveWidgets({
            nodes
        });
        let resolvedNode = nodes[0];
        if (resolvedNode.otid == otid) {
            descendantAssets = nodes[0].descendantAssets;
            rendered = nodes[0].content.rendered;
        }
    }
    return {
        rendered,
        assets: descendantAssets
    };
};
export const useWikiEditing = ({ otid })=>{
    /* manage wiki read/write/save either via VLM state or via local api calls
  writable are wikitext and mimetype
   */ let [wikitext, setWikitext] = useState(null);
    let [mimetype, setMimetype] = useState(null);
    let orgWikitext = null;
    let datatype = null;
    let caption = null;
    let saveEnabled = false;
    let saveWikiState = ()=>null;
    let revertWikiState = ()=>null;
    /* first test for available VLM context */ let vlmctx = useVlmContext();
    let vlmContextAvailable = !isNil(vlmctx.otid);
    // on local changes inform VLM
    useEffect(()=>{
        // a change check would also have to include "last send values"
        //~ let changed =
        //~ (wikitext !== null && vlmctx.text != wikitext) ||
        //~ (mimetype !== null && vlmctx.mimetype != mimetype)
        if (vlmContextAvailable && vlmctx.setWikiState) {
            vlmctx.setWikiState(wikitext, mimetype);
        }
    }, [
        wikitext,
        mimetype
    ]);
    // on vlm changes inform local read/write values
    useEffect(()=>{
        if (!isNil(vlmctx.mimetype)) setMimetype(vlmctx.mimetype);
        if (!isNil(vlmctx.text)) setWikitext(vlmctx.text);
    }, [
        vlmctx.text,
        vlmctx.mimetype
    ]);
    // if VlmWikiStateProvider available, use readonly values
    if (vlmContextAvailable) {
        // datatype is readonly we can copy it directly
        datatype = vlmctx.datatype;
        otid = vlmctx.otid;
        orgWikitext = vlmctx.text;
        saveEnabled = vlmctx.text != wikitext;
        saveWikiState = null; // consumer switch a save button on availabilty
        revertWikiState = null;
    }
    /* if no VLM context available use local load/save */ // also disabled if vlmctx.otid === null
    let api_enabled = otid && !vlmContextAvailable;
    let { data, error, reload: reloadWikiCode, loading } = useApi('filter/getWikiCode', {
        id: otid,
        resolve: true
    }, {
        autoreset: false,
        partReloadable: true,
        condition: api_enabled
    });
    let callApi = useActions(Actions.callApi);
    if (api_enabled && data) {
        let { wikiCode } = data;
        saveEnabled = wikitext !== null && wikitext != wikiCode.text || mimetype !== null && mimetype != wikiCode.mimetype;
        // use api-date if not modifications available
        datatype = datatype ?? wikiCode.datatype;
        mimetype = mimetype ?? wikiCode.mimetype;
        caption = caption ?? wikiCode.caption;
        wikitext = wikitext ?? wikiCode.text;
        orgWikitext = wikiCode.text;
        saveWikiState = ()=>{
            // save call
            let params = {
                id: otid,
                text: wikitext
            };
            if (wikiCode.mimetype != mimetype) {
                params = {
                    ...params,
                    mimetype
                };
            }
            callApi('saveWikiText', params, reloadWikiCode);
        };
        revertWikiState = ()=>{
            setWikitext(wikiCode.text);
            setMimetype(wikiCode.mimetype);
        };
    }
    return {
        saveWikiState,
        revertWikiState,
        wikitext: wikitext || '',
        setWikitext,
        orgWikitext: orgWikitext || '',
        mimetype,
        setMimetype,
        saveEnabled,
        otid,
        datatype,
        caption
    };
};
const EMPTY_PREVIEW = {
    rendered: {}
};
export const useWikiPreview = ({ text, otid, mimetype, datatype = 1, active = true, debounce = 500, debug = false, storeKey })=>{
    let setWikiPreview = useActions(Actions.setWikiPreview);
    let [debounced_text] = debounce ? useDebounce(text, debounce) : [
        text
    ];
    // also normalize null to '', should not trigger two times if null/'' switches
    let params = {
        text: debounced_text || '',
        debug
    };
    // either otid is given or datatype
    if (otid) {
        params = {
            ...params,
            otid
        };
    } else {
        params = {
            ...params,
            mimetype,
            datatype
        };
    }
    let { data, loading } = useApi('getWikiPreviewJSON', params, {
        condition: active,
        autoreset: false,
        method: 'post'
    });
    let preview = data || EMPTY_PREVIEW;
    // preview storing
    useEffect(()=>{
        if (storeKey && preview != EMPTY_PREVIEW) {
            setWikiPreview(storeKey, preview);
        }
    }, [
        storeKey,
        preview
    ]);
    //~ let previousStoreKey = usePrevious(storeKey)
    //~ let previousStoredPreview = useSelector(root => root.data.wiki.preview[previousStoreKey])
    //~ useEffect(() => {
    //~ if (previousStoredPreview) {
    //~ setWikiPreview(previousStoreKey, null)
    //~ }
    //~ }, [storeKey, previousStoreKey, previousStoredPreview])
    return {
        preview: data ?? {
            rendered: {}
        },
        reload: ()=>{}
    };
};
